import { useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import Papa from 'papaparse'
import { useTenant } from '@/contexts'
import { useAppContext } from '@/hooks/routing/useAppContext'
import { useAircraftList } from '@/hooks/useAircraft'
import { fuelTransactionKeys } from '@/hooks/useFuelTransactions'
import {
  bulkCreateFuelTransactions,
  fetchExistingExternalRefs,
  fetchActiveContractsWithAircraft,
} from '@/lib/api/fuel-transactions'
import {
  detectColumnMapping,
  resolveImportRows,
  computeSummary,
  type ColumnMapping,
  type ParsedFuelRow,
  type CsvParseResult,
  type FuelCsvField,
} from '@/lib/csv/fuel-csv-parser'
import type { FuelTransactionFormData } from '@/types/fuel-transactions'
import { broadcastInvalidation } from '@/lib/sync'
import { toast } from 'sonner'

// ============================================
// Types
// ============================================

export type ImportStep = 'upload' | 'mapping' | 'preview' | 'importing' | 'complete'

export interface ImportState {
  step: ImportStep
  fileName: string | null
  externalSystem: string
  headers: string[]
  rawRows: Record<string, string>[]
  mapping: ColumnMapping
  parsedRows: ParsedFuelRow[]
  summary: CsvParseResult['summary'] | null
  importProgress: { current: number; total: number }
  importResult: { inserted: number; errors: number } | null
}

const INITIAL_STATE: ImportState = {
  step: 'upload',
  fileName: null,
  externalSystem: 'Titan Aviation Fuels',
  headers: [],
  rawRows: [],
  mapping: new Map(),
  parsedRows: [],
  summary: null,
  importProgress: { current: 0, total: 0 },
  importResult: null,
}

// ============================================
// Hook
// ============================================

export function useFuelCsvImport() {
  const [state, setState] = useState<ImportState>({ ...INITIAL_STATE })
  const { currentOrganization } = useTenant()
  const { locationId } = useAppContext()
  const { data: aircraft } = useAircraftList()
  const queryClient = useQueryClient()

  /**
   * Parse a CSV file and auto-detect column mapping
   */
  const parseFile = useCallback((file: File, externalSystem: string) => {
    setState((prev) => ({ ...prev, fileName: file.name, externalSystem }))

    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      complete: (results) => {
        const allRows = results.data as string[][]
        if (allRows.length < 2) {
          toast.error('CSV file must have at least a header row and one data row')
          return
        }

        const headers = allRows[0].map((h) => h.trim())
        const mapping = detectColumnMapping(headers)

        // Convert data rows to Record<string, string> keyed by index
        const rawRows = allRows.slice(1).map((row) => {
          const obj: Record<string, string> = {}
          row.forEach((val, idx) => {
            obj[String(idx)] = val
          })
          return obj
        })

        setState((prev) => ({
          ...prev,
          step: 'mapping',
          headers,
          rawRows,
          mapping,
        }))
      },
      error: (error) => {
        toast.error('Failed to parse CSV file', { description: error.message })
      },
    })
  }, [])

  /**
   * Update a column mapping entry
   */
  const updateMapping = useCallback((columnIndex: number, field: FuelCsvField | null) => {
    setState((prev) => {
      const newMapping = new Map(prev.mapping)
      if (field === null) {
        newMapping.delete(columnIndex)
      } else {
        // Remove this field from any other column first
        for (const [idx, f] of newMapping.entries()) {
          if (f === field) newMapping.delete(idx)
        }
        newMapping.set(columnIndex, field)
      }
      return { ...prev, mapping: newMapping }
    })
  }, [])

  /**
   * Resolve rows using current mapping and org data
   */
  const resolveRows = useCallback(async () => {
    const organizationId = currentOrganization?.id
    if (!organizationId || !locationId) {
      toast.error('Missing organization or location context')
      return
    }

    try {
      // Fetch external refs + contracts in parallel
      const [existingRefs, contracts] = await Promise.all([
        fetchExistingExternalRefs(organizationId, state.externalSystem),
        fetchActiveContractsWithAircraft(organizationId),
      ])

      const parsedRows = resolveImportRows(
        state.rawRows,
        state.mapping,
        state.headers,
        {
          aircraft: aircraft ?? [],
          contracts,
          existingExternalRefs: existingRefs,
          locationId,
        }
      )

      const summary = computeSummary(parsedRows)

      setState((prev) => ({
        ...prev,
        step: 'preview',
        parsedRows,
        summary,
      }))
    } catch (error) {
      toast.error('Failed to resolve import data', {
        description: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }, [currentOrganization?.id, locationId, state.rawRows, state.mapping, state.headers, state.externalSystem, aircraft])

  /**
   * Execute import — insert ready + warning rows
   */
  const executeImport = useCallback(async () => {
    const organizationId = currentOrganization?.id
    if (!organizationId || !locationId) return

    const importableRows = state.parsedRows.filter(
      (r) => r.status === 'ready' || r.status === 'warning'
    )

    if (importableRows.length === 0) {
      toast.error('No importable rows')
      return
    }

    setState((prev) => ({
      ...prev,
      step: 'importing',
      importProgress: { current: 0, total: importableRows.length },
    }))

    // Convert ParsedFuelRow to FuelTransactionFormData
    const formRows: FuelTransactionFormData[] = importableRows.map((row) => ({
      location_id: locationId,
      client_id: row.clientId!,
      aircraft_id: row.aircraftId!,
      fuel_type: row.fuelType!,
      quantity_gallons: row.gallons!,
      price_per_gallon: row.unitPrice!,
      fueled_at: row.invoiceDate! + 'T12:00:00Z',
      lease_contract_id: row.leaseContractId,
      source: 'csv_import',
      external_reference: row.invoiceNumber,
      external_system: state.externalSystem,
      status: 'completed',
    }))

    try {
      const result = await bulkCreateFuelTransactions(organizationId, formRows)

      // Invalidate queries
      const keys = [
        fuelTransactionKeys.lists(),
        fuelTransactionKeys.summaries(),
        fuelTransactionKeys.commitments(),
      ] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      queryClient.invalidateQueries({ queryKey: ['map-allocations'] })
      broadcastInvalidation([...keys, ['map-allocations'] as const])

      setState((prev) => ({
        ...prev,
        step: 'complete',
        importProgress: { current: importableRows.length, total: importableRows.length },
        importResult: { inserted: result.inserted, errors: result.errors.length },
      }))

      if (result.inserted > 0) {
        toast.success(`Imported ${result.inserted} fuel transaction${result.inserted > 1 ? 's' : ''}`)
      }
      if (result.errors.length > 0) {
        toast.error(`${result.errors.length} row${result.errors.length > 1 ? 's' : ''} failed to import`)
      }
    } catch (error) {
      toast.error('Import failed', {
        description: error instanceof Error ? error.message : 'Unknown error',
      })
      // Go back to preview so user can retry
      setState((prev) => ({ ...prev, step: 'preview' }))
    }
  }, [currentOrganization?.id, locationId, state.parsedRows, state.externalSystem, queryClient])

  /**
   * Reset wizard to initial state
   */
  const reset = useCallback(() => {
    setState({ ...INITIAL_STATE })
  }, [])

  return {
    state,
    parseFile,
    updateMapping,
    resolveRows,
    executeImport,
    reset,
  }
}
